import { ref, computed } from 'vue'
import type { Ref, UnwrapRef } from 'vue'
import type { FormField, FormState, Page } from '@/types'
import { useQuestionnaireStore } from '@/store/questionaireStore'
import { getComponent } from '../components/questionaire/componentTypes'

export function useForm(rawSchema: Page[]) {
  const store = useQuestionnaireStore()
  const flatFields: FormField[] = []

  if (Array.isArray(rawSchema)) {
    rawSchema.forEach((page) => {
      if (Array.isArray(page.items)) {
        page.items.forEach((field) => {
          const updatedField = {
            ...field,
            component: getComponent(field.type)
          }

          flatFields.push(updatedField)
          if (field.value !== undefined && field.value !== '') {
            store.updateFormData(field.uuid, field.value)
          }
        })
      }
    })
  }

  let initialActiveField = flatFields[0] || {} // Default to first field
  for (let i = flatFields.length - 1; i >= 0; i--) {
    if (
      store.formData[flatFields[i].uuid] !== undefined &&
      store.formData[flatFields[i].uuid] !== '' &&
      store.formData[flatFields[i].uuid] !== null
    ) {
      initialActiveField = flatFields[i]
      break
    }
  }

  const initialActiveIndex = flatFields.findIndex((field) => field.uuid === initialActiveField.uuid)

  const formState: Ref<UnwrapRef<FormState>> = ref<FormState>({
    activeFieldId: initialActiveField.uuid || '',
    activeFieldIndex: initialActiveIndex !== -1 ? initialActiveIndex : 0,
    valid: true,
    next: true,
    formLength: flatFields.length,
    isLastField: initialActiveIndex === flatFields.length + 1,
    isFirstField: computed(() => formState.value.activeFieldIndex === 0),
    errorLength: 0
  })

  function validateField(value: boolean) {
    formState.value.valid = value
  }

  function findNextField(currentIndex: number): number {
    const currentField = flatFields[currentIndex]

    if (currentField.jump_to && currentField.jump_to.length > 0) {
      const jumpCondition = currentField.jump_to.find((jump) => {
        if (currentField.type === 'yesno') {
          const formDataValue = store.formData[currentField.uuid]
          const jumpValueLower = jump.value.toLowerCase()
          return (
            ((jumpValueLower === 'yes' || jumpValueLower === 'ja') && formDataValue === true) ||
            ((jumpValueLower === 'no' || jumpValueLower === 'nein') && formDataValue === false)
          )
        }
        return jump.value === store.formData[currentField.uuid]
      })
      if (jumpCondition) {
        if (jumpCondition.uuid === 'END') {
          setToLastField()
          return flatFields.length // Jump to the last field
        }
        const jumpIndex = flatFields.findIndex((field) => field.uuid === jumpCondition.uuid)
        if (jumpIndex !== -1) {
          return jumpIndex
        }
      }
    }
    return currentIndex + 1
  }

  function onSubmit() {
    if (formState.value.errorLength > 0) {
      validateField(false)
    } else {
      validateField(true)
      next()
    }
  }

  function back() {
    if (formState.value.activeFieldIndex > 0) {
      formState.value.next = false
      formState.value.activeFieldIndex--
      formState.value.activeFieldId = flatFields[formState.value.activeFieldIndex].uuid
      formState.value.isLastField = false
    }

    formState.value.valid = true
    formState.value.errorLength = 0
  }

  function next() {
    formState.value.next = true
    const nextIndex = findNextField(formState.value.activeFieldIndex)
    formState.value.activeFieldIndex = nextIndex
    formState.value.isLastField = nextIndex === flatFields.length
    if (!formState.value.isLastField) {
      formState.value.activeFieldId = flatFields[nextIndex].uuid
    }
  }

  function jumpToField(uuid: string) {
    const index = flatFields.findIndex((field) => field.uuid === uuid)
    if (index !== -1) {
      formState.value.activeFieldIndex = index
      formState.value.activeFieldId = uuid
      formState.value.isLastField = index === flatFields.length
    }
  }

  function setToLastField() {
    formState.value.isLastField = true
  }

  function getFieldByUuid(uuid: string) {
    return flatFields.find((field) => field.uuid === uuid)
  }

  function updateField(uuid: string, value: any) {
    store.updateFormData(uuid, value)
  }

  return {
    formState,
    validateField,
    onSubmit,
    back,
    next,
    jumpToField,
    setToLastField,
    getFieldByUuid,
    updateField,
    fields: flatFields
  }
}
